import { createTheme } from '@mui/material/styles';

// Design system accent colors
const accentColors = {
  dark: {
    primary: '#BB86FC',    // Purple
    secondary: '#03DAC6',  // Teal
  },
  light: {
    primary: '#6366f1',    // Indigo
    secondary: '#8b5cf6',  // Violet
  },
};

// Define a function that takes themeMode as a parameter
const getTheme = (themeMode) => {
  const isDark = themeMode === 'dark';
  const accent = isDark ? accentColors.dark : accentColors.light;

  return createTheme({
    palette: {
      mode: themeMode,
      background: {
        default: isDark ? '#121212' : '#FFFFFF',
        paper: isDark ? '#1e1e23' : '#FFFFFF',
        actions: isDark ? '#171717' : '#f8f8f8',
        highlight: isDark ? 'rgba(100,100,100,0.05)' : 'rgba(0, 0, 0, 0.04)',
        overlay: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
        modal: isDark ? '#000000' : '#ddd',
      },
      primary: {
        main: accent.primary,
        light: isDark ? '#C77CFF' : '#818cf8',
        dark: isDark ? '#3d216a' : '#4f46e5',
      },
      secondary: {
        main: accent.secondary,
      },
      accent: {
        primary: accent.primary,
        secondary: accent.secondary,
        glow: isDark ? 'rgba(187, 134, 252, 0.15)' : 'rgba(99, 102, 241, 0.1)',
      },
      error: {
        main: isDark ? '#CF6679' : '#B00020',
      },
      success: {
        main: isDark ? '#03DAC6' : '#10b981',
      },
      border: {
        main: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
        highlight: isDark ? accent.primary : accent.primary,
        subtle: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      },
      text: {
        primary: isDark ? 'rgba(255,255,255,0.87)' : 'rgba(33,33,33,1)',
        secondary: isDark ? 'rgba(255,255,255,0.30)' : 'rgb(137, 143, 151)',
        disabled: isDark ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)',
      },
      divider: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
    },
    shape: {
      borderRadius: 12,
      borderRadiusLg: 16,
      borderRadiusSm: 8,
    },
    typography: {
      fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
    },
    components: {
        MuiButton: {
          styleOverrides: {
            root: {
            textTransform: 'none',
            borderRadius: 12,
            },
        },
        },
        MuiMenu: {
            styleOverrides: {
              paper: {
            borderRadius: 14,
            boxShadow: isDark
              ? '0px 4px 20px rgba(0, 0, 0, 0.4)'
              : '0px 4px 20px rgba(0, 0, 0, 0.1)',
              },
        },
        },
        MuiPaginationItem: {
          styleOverrides: {
            root: {
            borderRadius: 8,
              '&.Mui-selected': {
              backgroundColor: accent.primary,
              color: isDark ? '#121212' : '#FFFFFF',
              '&:hover': {
                backgroundColor: accent.primary,
              },
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 30, 35, 0.95) 0%, rgba(20, 20, 25, 0.98) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            boxShadow: isDark
              ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.05) inset'
              : '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255,255,255,0.8) inset',
          },
          paperFullScreen: {
            borderRadius: 0,
            border: 'none',
            // Match the desktop glassmorphism style for consistency
            background: isDark
              ? 'linear-gradient(180deg, rgba(30, 30, 35, 0.99) 0%, rgba(22, 22, 26, 1) 100%)'
              : 'linear-gradient(180deg, rgba(255, 255, 255, 0.99) 0%, rgba(248, 250, 252, 1) 100%)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
            fontSize: '1rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            padding: '20px 24px 12px',
            borderBottom: 'none',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
            padding: '16px 24px',
            '&.MuiDialogContent-dividers': {
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            },
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
            fontSize: '0.85rem',
            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
            lineHeight: 1.6,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px 20px',
            gap: '12px',
            '& .MuiButton-root': {
              fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
              fontSize: '0.85rem',
              fontWeight: 500,
              padding: '8px 20px',
              borderRadius: 10,
            },
            '& .MuiButton-contained': {
              boxShadow: `0 4px 14px -2px ${isDark ? 'rgba(187, 134, 252, 0.3)' : 'rgba(99, 102, 241, 0.3)'}`,
              '&:hover': {
                boxShadow: `0 6px 20px -2px ${isDark ? 'rgba(187, 134, 252, 0.4)' : 'rgba(99, 102, 241, 0.4)'}`,
              },
            },
            '& .MuiButton-text, & .MuiButton-outlined': {
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              },
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
              borderRadius: 12,
              '& fieldset': {
                borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              },
              '&:hover fieldset': {
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              },
              '&.Mui-focused fieldset': {
                borderColor: accent.primary,
                borderWidth: '1.5px',
                boxShadow: `0 0 0 3px ${isDark ? 'rgba(187, 134, 252, 0.1)' : 'rgba(99, 102, 241, 0.08)'}`,
              },
            },
            '& .MuiInputLabel-root': {
              fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
              fontSize: '0.85rem',
            },
            '& .MuiInputBase-input': {
              fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
              fontSize: '0.9rem',
            },
          },
        },
      },
    },
  });
};

export default getTheme;
